/**
 * Blog Article Filters JavaScript
 * Handles mobile drawer interactions and query-param based filtering.
 *
 * Filter state lives in the URL (category, meal, food, difficulty,
 * duration, sort_by). When any filter is active, results are
 * fetched from the article-filter app proxy and rendered client-side.
 * Without filters the original server-rendered grid is restored.
 */

const FILTER_PARAM_KEYS = [
  'category',
  'meal',
  'food',
  'difficulty',
  'duration',
];

const GROUP_PARAM_KEYS = ['category', 'meal', 'food'];

// URL params are English; internal group ids (= metaobject gruppe values,
// data-filter-group attributes) stay German. Translate at the URL boundary.
const GROUP_TO_PARAM = {
  kategorie: 'category',
  mahlzeit: 'meal',
  lebensmittel: 'food',
  schwierigkeit: 'difficulty',
  dauer: 'duration',
};
const paramFor = group => GROUP_TO_PARAM[group] || group;

// theme sort_by -> proxy sort param
const SORT_PARAM_MAP = {
  published_desc: 'date_desc',
  published_asc: 'date_asc',
  title_asc: 'title_asc',
  title_desc: 'title_desc',
  preparation_time_asc: 'prep_asc',
};

const CLOCK_ICON_SVG = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 stroke-aubergine"><path d="M15.9993 29.3334C23.3631 29.3334 29.3327 23.3639 29.3327 16.0001C29.3327 8.63628 23.3631 2.66675 15.9993 2.66675C8.63555 2.66675 2.66602 8.63628 2.66602 16.0001C2.66602 23.3639 8.63555 29.3334 15.9993 29.3334Z" stroke="" stroke-width="2.66667"/><path d="M16 10.6666V16L18.6667 18.6666" stroke="" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

class BlogArticleFilters {
  constructor() {
    this.grid = document.querySelector('.collection-grid');
    this.heading = document.querySelector('[data-blog-heading]');
    this.emptyState = document.querySelector('[data-blog-empty-state]');
    this.nativeLoadMore = document.querySelector('[data-load-more-container]');

    const gridData = this.grid ? this.grid.dataset : {};
    this.proxyUrl = gridData.proxyUrl || '/apps/article-filter/articles';
    this.locale = gridData.locale || '';
    this.blogUrl = gridData.blogUrl || window.location.pathname;
    this.headingDefault =
      (this.heading && this.heading.dataset.headingDefault) ||
      gridData.headingDefault ||
      '';
    // Everything before "/blogs/" is the (possibly empty) locale prefix.
    // Never hard-code the prefix - it is derived from the Liquid-rendered
    // blog.url which already carries the active locale.
    this.localePrefix = this.blogUrl.includes('/blogs/')
      ? this.blogUrl.split('/blogs/')[0]
      : '';

    // Detached copy of the server-rendered grid so we can restore it
    // without a reload when all filters are removed.
    this.initialGridHTML = this.grid ? this.grid.innerHTML : '';
    this.initialCountTexts = Array.from(
      document.querySelectorAll('[data-article-count-label]')
    ).map(el => el.textContent.trim());

    this.labelMap = this.parseLabelMap();

    this.isFilteredMode = false;
    this.fallbackMode = false;
    this.fetchToken = 0;
    this.filteredState = { page: 1, pages: 1, total: 0 };
    this.filteredLoadMoreContainer = null;
    this.filteredLoadMoreButton = null;
    this.fallbackObserver = null;

    this.init();
  }

  init() {
    this.normalizeLegacyParams();
    this.setupMobileDrawer();
    this.setupSortDrawer();
    this.setupSortLinks();
    this.setupFilterOptions();
    this.setupSchwierigkeitFilter();
    this.setupDauerFilter();
    this.setupResetLinks();

    window.addEventListener('popstate', () => {
      this.applyFromUrl();
    });

    this.applyFromUrl();
  }

  /**
   * Old links may still carry German param names (?kategorie=...). Rewrite
   * them to the English names once on load so filtering and header hiding
   * keep working for shared/bookmarked URLs.
   */
  // eslint-disable-next-line class-methods-use-this
  normalizeLegacyParams() {
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    Object.entries(GROUP_TO_PARAM).forEach(([legacy, current]) => {
      const value = params.get(legacy);
      if (value !== null) {
        if (!params.get(current)) params.set(current, value);
        params.delete(legacy);
        changed = true;
      }
    });
    if (changed) {
      const query = params.toString();
      const url = query
        ? `${window.location.pathname}?${query}`
        : window.location.pathname;
      window.history.replaceState({}, '', url);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  parseLabelMap() {
    const script = document.querySelector('[data-kategorie-labels]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  labelFor(handle) {
    // Comma-separated handles (OR filter, e.g. ?category=herbst,halloween)
    // -> "Herbst, Halloween & Kürbis"
    if (String(handle).includes(',')) {
      const labels = String(handle)
        .split(',')
        .filter(Boolean)
        .map(single => this.labelFor(single));
      if (labels.length <= 1) return labels[0] || '';
      return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
    }
    if (this.labelMap[handle]) return this.labelMap[handle];
    // Fallback: prettified handle ("meal-prep" -> "Meal Prep")
    return String(handle)
      .split('-')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /* ------------------------------------------------------------------ */
  /* URL state                                                           */
  /* ------------------------------------------------------------------ */

  // eslint-disable-next-line class-methods-use-this
  getParams() {
    return new URLSearchParams(window.location.search);
  }

  setParams(mutator) {
    const params = this.getParams();
    mutator(params);
    // Drop empty values so the URL stays clean
    FILTER_PARAM_KEYS.concat(['sort_by']).forEach(key => {
      if (params.get(key) === '' || params.get(key) === null) {
        params.delete(key);
      }
    });
    const query = params.toString();
    const url = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;
    window.history.pushState({}, '', url);
    this.applyFromUrl();
  }

  toggleGroupParam(group, value) {
    this.setParams(params => {
      if (params.get(group) === value) {
        params.delete(group);
      } else {
        params.set(group, value);
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  hasActiveFilters(params) {
    return FILTER_PARAM_KEYS.some(key => {
      const value = params.get(key);
      return value !== null && value !== '';
    });
  }

  /* ------------------------------------------------------------------ */
  /* Apply state                                                         */
  /* ------------------------------------------------------------------ */

  applyFromUrl() {
    const params = this.getParams();

    const searchHeader = document.querySelector('[data-blog-search-header]');
    if (searchHeader) {
      const filtersActive = this.hasActiveFilters(params);
      searchHeader.classList.toggle('hidden', filtersActive);
      // Mirror theme.liquid's /tagged/ spacing: without the search header the
      // content needs the same top padding a tagged page used to get.
      const main = document.getElementById('MainContent');
      if (main) {
        main.classList.toggle('md:pt-[50px]', filtersActive);
        main.classList.toggle('md:pt-0', !filtersActive);
      }
    }

    this.updateFilterOptionStates(params);
    this.updateSchwierigkeitButtons(params);
    this.updateCategoryTiles(params);
    this.updateSortButtonText();
    this.updateHeading(params);

    if (!this.grid) return;

    if (this.hasActiveFilters(params)) {
      this.enterFilteredMode(params);
    } else {
      this.exitFilteredMode(params);
    }
  }

  async enterFilteredMode(params, page = 1, append = false) {
    this.stopFallbackObserver();
    this.fetchToken += 1;
    const token = this.fetchToken;

    try {
      const response = await fetch(this.buildProxyUrl(params, page));
      if (!response.ok) {
        throw new Error(`Proxy error: ${response.status}`);
      }
      const data = await response.json();
      if (token !== this.fetchToken) return;

      this.isFilteredMode = true;
      this.fallbackMode = false;
      this.filteredState = {
        page: data.page || page,
        pages: data.pages || 1,
        total: typeof data.total === 'number' ? data.total : 0,
      };
      this.renderArticles(data.articles || [], append);
      this.updateArticleCount(this.filteredState.total);
      this.toggleNativeLoadMore(false);
      this.updateFilteredLoadMore();
      this.toggleEmptyState(this.filteredState.total > 0);
    } catch (error) {
      if (token !== this.fetchToken) return;
      // Proxy unavailable - fall back to client-side show/hide over the
      // currently loaded (server-rendered) cards.
      this.applyClientFallback(params);
    }
  }

  exitFilteredMode(params) {
    this.stopFallbackObserver();
    this.fetchToken += 1;
    this.isFilteredMode = false;
    this.fallbackMode = false;

    if (this.grid.innerHTML !== this.initialGridHTML) {
      this.grid.innerHTML = this.initialGridHTML;
    }
    this.grid.querySelectorAll('.article-item').forEach(item => {
      // eslint-disable-next-line no-param-reassign
      item.style.display = '';
    });

    this.restoreArticleCount();
    this.toggleNativeLoadMore(true);
    if (this.filteredLoadMoreContainer) {
      this.filteredLoadMoreContainer.classList.add('hidden');
    }
    this.applyClientSorting(params.get('sort_by') || 'published_desc');
    this.toggleEmptyStateFromVisible();
  }

  buildProxyUrl(params, page) {
    const query = new URLSearchParams();
    GROUP_PARAM_KEYS.concat(['difficulty']).forEach(key => {
      const value = params.get(key);
      if (value) query.set(key, value);
    });
    const dauer = params.get('duration');
    if (dauer) query.set('dauer_max', dauer);
    const sortBy = params.get('sort_by');
    if (sortBy && SORT_PARAM_MAP[sortBy]) {
      query.set('sort', SORT_PARAM_MAP[sortBy]);
    }
    if (this.locale) query.set('locale', this.locale);
    query.set('page', String(page));
    return `${this.proxyUrl}?${query.toString()}`;
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  renderArticles(articles, append) {
    const html = articles.map(article => this.renderCard(article)).join('');
    if (append) {
      this.grid.insertAdjacentHTML('beforeend', html);
    } else {
      this.grid.innerHTML = html;
    }
  }

  /**
   * Replicates the markup of snippets/article-card.liquid (incl. the
   * .article-item wrapper from sections/blog-article-grid.liquid).
   */
  renderCard(article) {
    const url = this.localePrefix + (article.url || '');
    const title = article.title || '';
    const kategorien = Array.isArray(article.kategorien)
      ? article.kategorien
      : [];
    const prepTime = article.preparationTime;
    const prepMinutes = parseInt(prepTime, 10);

    const badgesHtml = kategorien
      .slice(0, 2)
      .map(handle => {
        const label = this.labelFor(handle);
        return `<span class="badge-article-tag bg-pflaume" title="${escapeHtml(
          label
        )}">${escapeHtml(truncateText(label, 15))}</span>`;
      })
      .join('');

    const imageHtml = article.image
      ? `<img src="${escapeHtml(
          sizedImageUrl(article.image, 880)
        )}" alt="${escapeHtml(
          article.imageAlt || title
        )}" width="440" height="440" class="w-full h-full object-cover object-center aspect-square max-h-48 max-w-48 md:max-h-110 md:max-w-110" loading="lazy">`
      : `<div class="w-full h-full object-cover object-center aspect-square max-h-48 max-w-48 md:max-h-110 md:max-w-110 bg-aubergine/10"></div>`;

    const prepHtml =
      prepTime !== null && prepTime !== undefined && prepTime !== ''
        ? `${CLOCK_ICON_SVG}<span>${escapeHtml(
            Number.isNaN(prepMinutes) ? String(prepTime) : `${prepMinutes} Min`
          )}</span>`
        : '';

    const itemAttributes = [
      `data-published-date="${escapeHtml(article.publishedAt || '')}"`,
    ];
    if (!Number.isNaN(prepMinutes)) {
      itemAttributes.push(`data-preparation-time="${prepMinutes}"`);
    }
    if (article.schwierigkeit) {
      itemAttributes.push(
        `data-schwierigkeit="${escapeHtml(article.schwierigkeit)}"`
      );
    }
    if (kategorien.length > 0) {
      itemAttributes.push(
        `data-kategorien="${escapeHtml(kategorien.join(','))}"`
      );
    }

    return `<div class="article-item js-article-item" ${itemAttributes.join(
      ' '
    )}>
      <a href="${escapeHtml(
        url
      )}" class="article-card flex flex-col w-full max-w-48 md:max-w-110 items-center gap-y-6 h-full" aria-label="${escapeHtml(
        title
      )}">
        ${imageHtml}
        <div class="flex flex-col gap-y-2 items-start lg:items-center text-left lg:text-center h-23 w-full">
          <span class="h-fit flex justify-start lg:justify-center gap-2 flex-wrap lg:flex-nowrap w-full">${badgesHtml}</span>
          <h3 class="article-card-title h-product text-aubergine break-all" title="${escapeHtml(
            title
          )}">${escapeHtml(truncateText(title, 30))}</h3>
          <p class="p-medium flex items-start lg:items-center gap-1.5 text-aubergine h-5">${prepHtml}</p>
        </div>
      </a>
    </div>`;
  }

  /* ------------------------------------------------------------------ */
  /* Filtered "load more"                                                */
  /* ------------------------------------------------------------------ */

  updateFilteredLoadMore() {
    if (!this.filteredLoadMoreContainer) {
      const container = document.createElement('div');
      container.className = 'w-full flex flex-col items-center gap-6 mt-8';
      container.setAttribute('data-filtered-load-more', '');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn bg-gurke hover:bg-gurke/80 transition-colors';
      const nativeText = document.querySelector(
        '[data-load-more-button] [data-button-text]'
      );
      button.textContent = nativeText
        ? nativeText.textContent.trim()
        : 'Mehr laden';
      button.addEventListener('click', () => {
        if (this.filteredState.page >= this.filteredState.pages) return;
        button.setAttribute('disabled', '');
        this.enterFilteredMode(
          this.getParams(),
          this.filteredState.page + 1,
          true
        ).finally(() => {
          button.removeAttribute('disabled');
        });
      });

      container.appendChild(button);
      this.grid.insertAdjacentElement('afterend', container);
      this.filteredLoadMoreContainer = container;
      this.filteredLoadMoreButton = button;
    }

    const hasMore = this.filteredState.page < this.filteredState.pages;
    this.filteredLoadMoreContainer.classList.toggle('hidden', !hasMore);
  }

  toggleNativeLoadMore(show) {
    if (!this.nativeLoadMore) return;
    this.nativeLoadMore.style.display = show ? '' : 'none';
  }

  /* ------------------------------------------------------------------ */
  /* Client-side fallback filtering (proxy unavailable)                  */
  /* ------------------------------------------------------------------ */

  applyClientFallback(params) {
    this.isFilteredMode = false;
    this.fallbackMode = true;

    // Make sure we filter over the original server-rendered cards
    if (this.grid.innerHTML !== this.initialGridHTML) {
      this.grid.innerHTML = this.initialGridHTML;
    }
    if (this.filteredLoadMoreContainer) {
      this.filteredLoadMoreContainer.classList.add('hidden');
    }
    this.toggleNativeLoadMore(true);

    this.filterLoadedCards(params);
    this.applyClientSorting(params.get('sort_by') || 'published_desc');
    this.updateArticleCount(this.countVisibleArticles());
    this.toggleEmptyStateFromVisible();
    this.startFallbackObserver();
  }

  filterLoadedCards(params) {
    const schwierigkeit = params.get('difficulty');
    const dauer = params.get('duration') ? parseInt(params.get('duration'), 10) : null;
    const groupValues = GROUP_PARAM_KEYS.map(key => params.get(key)).filter(
      Boolean
    );

    const articles = Array.from(this.grid.querySelectorAll('.article-item'));
    articles.forEach(articleEl => {
      let shouldShow = true;

      if (schwierigkeit) {
        const attr = articleEl.getAttribute('data-schwierigkeit');
        if (!attr || attr !== schwierigkeit) {
          shouldShow = false;
        }
      }

      if (shouldShow && dauer) {
        const prepTimeAttr = articleEl.getAttribute('data-preparation-time');
        const prepTime = prepTimeAttr ? parseInt(prepTimeAttr, 10) : NaN;
        if (Number.isNaN(prepTime) || prepTime > dauer) {
          shouldShow = false;
        }
      }

      if (shouldShow && groupValues.length > 0) {
        const kategorien = (articleEl.getAttribute('data-kategorien') || '')
          .split(',')
          .map(value => value.trim())
          .filter(Boolean);
        const matchesAll = groupValues.every(value =>
          kategorien.includes(value)
        );
        if (!matchesAll) {
          shouldShow = false;
        }
      }

      // eslint-disable-next-line no-param-reassign
      articleEl.style.display = shouldShow ? '' : 'none';
    });
  }

  startFallbackObserver() {
    if (this.fallbackObserver || !this.grid) return;
    // Re-filter cards appended by the native load-more while in fallback mode
    this.fallbackObserver = new MutationObserver(() => {
      if (!this.fallbackMode) return;
      this.filterLoadedCards(this.getParams());
      this.updateArticleCount(this.countVisibleArticles());
      this.toggleEmptyStateFromVisible();
    });
    this.fallbackObserver.observe(this.grid, { childList: true });
  }

  stopFallbackObserver() {
    if (this.fallbackObserver) {
      this.fallbackObserver.disconnect();
      this.fallbackObserver = null;
    }
  }

  /* ------------------------------------------------------------------ */
  /* UI state updates                                                    */
  /* ------------------------------------------------------------------ */

  // eslint-disable-next-line class-methods-use-this
  updateFilterOptionStates(params) {
    // Desktop dropdown option buttons
    document.querySelectorAll('[data-filter-option]').forEach(button => {
      const group = button.getAttribute('data-filter-group');
      const value = button.getAttribute('data-filter-value');
      const isActive = params.get(paramFor(group)) === value;

      const checkbox = button.querySelector('[data-filter-checkbox]');
      const tick = button.querySelector('[data-filter-tick]');
      if (checkbox) {
        checkbox.classList.toggle('bg-gurke', isActive);
        checkbox.classList.toggle('bg-white', !isActive);
      }
      if (tick) {
        tick.classList.toggle('hidden', !isActive);
      }
    });

    // Mobile drawer checkboxes
    document
      .querySelectorAll('input[type="checkbox"][data-filter-group]')
      .forEach(checkbox => {
        const group = checkbox.getAttribute('data-filter-group');
        const value = checkbox.getAttribute('data-filter-value');
        // eslint-disable-next-line no-param-reassign
        checkbox.checked = params.get(paramFor(group)) === value;
      });

    // Per-group count badges + clear links (desktop dropdowns)
    document.querySelectorAll('[data-filter-count-badge]').forEach(badge => {
      const group = badge.getAttribute('data-filter-count-badge');
      const hasValue = Boolean(params.get(group));
      badge.classList.toggle('hidden', !hasValue);
    });
    document.querySelectorAll('[data-filter-clear-wrap]').forEach(wrap => {
      const group = wrap.getAttribute('data-filter-clear-wrap');
      const hasValue = Boolean(params.get(paramFor(group)));
      wrap.classList.toggle('hidden', !hasValue);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  updateSchwierigkeitButtons(params) {
    const active = params.get('difficulty');
    document.querySelectorAll('[data-schwierigkeit-filter]').forEach(button => {
      const value = button.getAttribute('data-schwierigkeit-filter');
      const isActive = value === active;
      button.setAttribute(
        'data-schwierigkeit-active',
        isActive ? 'true' : 'false'
      );
      if (isActive) {
        button.classList.add('bg-gurke/10');
      } else {
        button.classList.remove('bg-gurke/10');
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  updateCategoryTiles(params) {
    const active = params.get('category');
    document.querySelectorAll('[data-kategorie-tile]').forEach(tile => {
      const isActive = tile.getAttribute('data-kategorie-tile') === active;
      const border = tile.querySelector('[data-tile-border]');
      const label = tile.querySelector('[data-tile-label]');
      const check = tile.querySelector('[data-tile-check]');
      if (border) {
        border.classList.toggle('border-erbse', isActive);
        border.classList.toggle('border-transparent', !isActive);
      }
      if (label) {
        label.classList.toggle('text-gurke', isActive);
        label.classList.toggle('text-aubergine', !isActive);
      }
      if (check) {
        check.classList.toggle('hidden', !isActive);
      }
    });
  }

  updateHeading(params) {
    if (!this.heading) return;

    const activeValues = GROUP_PARAM_KEYS.map(key => params.get(key)).filter(
      Boolean
    );

    if (activeValues.length === 1) {
      const label = this.labelFor(activeValues[0]);
      const defaultHeading = this.headingDefault;
      let text = label;
      if (
        defaultHeading &&
        !label.toLowerCase().endsWith(defaultHeading.toLowerCase())
      ) {
        text = `${label} ${defaultHeading}`;
      }
      this.heading.textContent = text;
    } else {
      this.heading.textContent = this.headingDefault;
    }
  }

  updateArticleCount(count) {
    document.querySelectorAll('[data-article-count-label]').forEach(el => {
      const template = el.getAttribute('data-count-template');
      if (template) {
        // eslint-disable-next-line no-param-reassign
        el.textContent = template.replace('[COUNT]', String(count));
      } else {
        // eslint-disable-next-line no-param-reassign
        el.textContent = String(count);
      }
    });
  }

  restoreArticleCount() {
    const labels = document.querySelectorAll('[data-article-count-label]');
    labels.forEach((el, index) => {
      if (this.initialCountTexts[index] !== undefined) {
        // eslint-disable-next-line no-param-reassign
        el.textContent = this.initialCountTexts[index];
      }
    });
  }

  countVisibleArticles() {
    return Array.from(this.grid.querySelectorAll('.article-item')).filter(
      el => el.style.display !== 'none'
    ).length;
  }

  toggleEmptyState(hasArticles) {
    if (!this.emptyState) return;
    this.emptyState.classList.toggle('hidden', hasArticles);
  }

  toggleEmptyStateFromVisible() {
    const visibleCount = this.countVisibleArticles();
    this.toggleEmptyState(visibleCount > 0);
    if (this.nativeLoadMore && !this.isFilteredMode) {
      this.nativeLoadMore.style.display = visibleCount > 0 ? '' : 'none';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Sorting                                                             */
  /* ------------------------------------------------------------------ */

  setupSortLinks() {
    document.addEventListener('click', event => {
      const link = event.target.closest('[data-sort-option]');
      if (!link) return;

      event.preventDefault();
      const option = link.getAttribute('data-sort-option');
      this.setParams(params => {
        if (option === 'published_desc') {
          params.delete('sort_by');
        } else {
          params.set('sort_by', option);
        }
      });
      this.closeSortDrawer();
    });
  }

  /**
   * Update sort button text based on URL parameter
   */
  // eslint-disable-next-line class-methods-use-this
  updateSortButtonText() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortBy = urlParams.get('sort_by') || 'published_desc';

    // Read the label from the corresponding menu link (which Liquid renders
    // via `| t`), so JS doesn't need its own translation table.
    const sortLink =
      document.querySelector(`[data-sort-option="${sortBy}"]`) ||
      document.querySelector('[data-sort-option="published_desc"]');
    if (!sortLink) return;
    const sortName = sortLink.textContent.trim();

    const sortButtonTexts = document.querySelectorAll(
      '[data-sort-button-text]'
    );
    sortButtonTexts.forEach(buttonText => {
      // eslint-disable-next-line no-param-reassign
      buttonText.textContent = sortName;
    });
  }

  /**
   * Client-side sorting of the currently rendered cards (used for the
   * unfiltered server-rendered grid and the fallback mode; in filtered
   * mode the proxy handles sorting).
   */
  applyClientSorting(sortBy) {
    if (!this.grid) return;

    const articles = Array.from(
      this.grid.querySelectorAll('.article-item')
    ).filter(el => el.style.display !== 'none');

    if (articles.length === 0) return;

    const articlesData = articles.map(articleEl => {
      const card = articleEl.querySelector('.article-card');
      const titleEl = card?.querySelector('.article-card-title');
      const dateEl = articleEl.getAttribute('data-published-date');
      const prepTimeAttr = articleEl.getAttribute('data-preparation-time');
      const prepTime = prepTimeAttr ? parseInt(prepTimeAttr, 10) : null;

      return {
        element: articleEl,
        title: titleEl?.textContent?.trim() || '',
        date: dateEl ? new Date(dateEl) : new Date(0),
        prepTime:
          prepTime !== null && !Number.isNaN(prepTime) ? prepTime : Infinity,
      };
    });

    articlesData.sort((a, b) => {
      switch (sortBy) {
        case 'published_asc':
          return a.date - b.date;
        case 'published_desc':
          return b.date - a.date;
        case 'title_asc':
          return a.title.localeCompare(b.title, 'de');
        case 'title_desc':
          return b.title.localeCompare(a.title, 'de');
        case 'preparation_time_asc':
          return a.prepTime - b.prepTime;
        default:
          return 0;
      }
    });

    articlesData.forEach(({ element }) => {
      this.grid.appendChild(element);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Filter option interactions                                          */
  /* ------------------------------------------------------------------ */

  setupFilterOptions() {
    document.addEventListener('click', event => {
      // Desktop dropdown option buttons
      const option = event.target.closest('[data-filter-option]');
      if (option) {
        event.preventDefault();
        const group = option.getAttribute('data-filter-group');
        const value = option.getAttribute('data-filter-value');
        this.toggleGroupParam(paramFor(group), value);
        return;
      }

      // Desktop dropdown clear buttons
      const clear = event.target.closest('[data-filter-clear]');
      if (clear) {
        event.preventDefault();
        const group = clear.getAttribute('data-filter-clear');
        this.setParams(params => params.delete(paramFor(group)));
        return;
      }

      // Category slider tiles
      const tile = event.target.closest('[data-kategorie-tile]');
      if (tile) {
        event.preventDefault();
        const value = tile.getAttribute('data-kategorie-tile');
        this.toggleGroupParam('category', value);
      }
    });
  }

  setupResetLinks() {
    document.addEventListener('click', event => {
      const reset = event.target.closest('[data-filter-reset]');
      if (!reset) return;

      event.preventDefault();
      this.setParams(params => {
        FILTER_PARAM_KEYS.forEach(key => params.delete(key));
      });
      this.closeFilterDrawer();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Mobile Filter Drawer                                                */
  /* ------------------------------------------------------------------ */

  setupMobileDrawer() {
    const drawerTrigger = document.querySelector(
      '[data-filter-drawer-trigger]'
    );
    const drawer = document.querySelector('[data-filter-drawer]');
    const overlay = document.querySelector('[data-filter-drawer-overlay]');
    const closeButton = document.querySelector('[data-filter-drawer-close]');
    const applyButton = document.querySelector('[data-filter-drawer-apply]');

    if (!drawerTrigger || !drawer || !overlay) return;

    this.filterDrawer = drawer;
    this.filterDrawerOverlay = overlay;

    // Open drawer
    drawerTrigger.addEventListener('click', () => {
      drawer.classList.remove('-translate-x-full');
      overlay.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    });

    closeButton?.addEventListener('click', () => this.closeFilterDrawer());
    overlay.addEventListener('click', () => this.closeFilterDrawer());

    // Apply filters: turn drawer checkbox selections into URL params
    applyButton?.addEventListener('click', () => {
      const selections = {};
      Object.keys(GROUP_TO_PARAM).forEach(group => {
        selections[group] = null;
      });
      drawer
        .querySelectorAll('input[type="checkbox"][data-filter-group]')
        .forEach(checkbox => {
          const group = checkbox.getAttribute('data-filter-group');
          if (checkbox.checked) {
            selections[group] = checkbox.getAttribute('data-filter-value');
          }
        });

      this.setParams(params => {
        Object.keys(selections).forEach(group => {
          // "kategorie" is only set via the category slider tiles; drawer
          // checkboxes cover mahlzeit/lebensmittel
          if (
            selections[group] === null &&
            !drawer.querySelector(`input[data-filter-group="${group}"]`)
          ) {
            return;
          }
          if (selections[group]) {
            params.set(paramFor(group), selections[group]);
          } else {
            params.delete(paramFor(group));
          }
        });
      });
      this.closeFilterDrawer();
    });

    // Handle details/summary for collapsible filter groups
    const details = drawer.querySelectorAll('details');
    details.forEach(detail => {
      const summary = detail.querySelector('summary');
      const chevron = summary?.querySelector('svg');

      // Set initial state
      if (chevron && detail.open) {
        chevron.style.transform = 'rotate(180deg)';
      }

      summary?.addEventListener('click', () => {
        // Toggle chevron rotation
        if (chevron) {
          setTimeout(() => {
            chevron.style.transform = detail.open
              ? 'rotate(180deg)'
              : 'rotate(0deg)';
          }, 0);
        }
      });
    });

    // Handle clear category buttons (clears the drawer selection; applied
    // on the apply button)
    const clearButtons = drawer.querySelectorAll('[data-clear-category]');
    clearButtons.forEach(button => {
      button.addEventListener('click', () => {
        const categoryId = button.getAttribute('data-clear-category');
        const checkboxes = drawer.querySelectorAll(
          `input[type="checkbox"][data-category="${categoryId}"]`
        );
        checkboxes.forEach(checkbox => {
          // eslint-disable-next-line no-param-reassign
          checkbox.checked = false;
        });
      });
    });

    // Handle checkbox single selection per category (radio button behavior)
    const checkboxes = drawer.querySelectorAll(
      'input[type="checkbox"][data-category]'
    );
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          const categoryId = checkbox.getAttribute('data-category');
          const otherCheckboxes = drawer.querySelectorAll(
            `input[type="checkbox"][data-category="${categoryId}"]:not([value="${checkbox.value}"])`
          );
          otherCheckboxes.forEach(otherCheckbox => {
            // eslint-disable-next-line no-param-reassign
            otherCheckbox.checked = false;
          });
        }
      });
    });
  }

  closeFilterDrawer() {
    if (!this.filterDrawer || !this.filterDrawerOverlay) return;
    this.filterDrawer.classList.add('-translate-x-full');
    this.filterDrawerOverlay.classList.add('hidden');
    document.body.style.overflow = '';
  }

  /* ------------------------------------------------------------------ */
  /* Mobile Sort Drawer                                                  */
  /* ------------------------------------------------------------------ */

  setupSortDrawer() {
    const drawerTrigger = document.querySelector('[data-sort-drawer-trigger]');
    const drawer = document.querySelector('[data-sort-drawer]');
    const overlay = document.querySelector('[data-sort-drawer-overlay]');
    const closeButton = document.querySelector('[data-sort-drawer-close]');

    if (!drawerTrigger || !drawer || !overlay) return;

    this.sortDrawer = drawer;
    this.sortDrawerOverlay = overlay;

    // Open drawer
    drawerTrigger.addEventListener('click', () => {
      drawer.classList.remove('-translate-x-full');
      overlay.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    });

    closeButton?.addEventListener('click', () => this.closeSortDrawer());
    overlay.addEventListener('click', () => this.closeSortDrawer());
  }

  closeSortDrawer() {
    if (!this.sortDrawer || !this.sortDrawerOverlay) return;
    this.sortDrawer.classList.add('-translate-x-full');
    this.sortDrawerOverlay.classList.add('hidden');
    document.body.style.overflow = '';
  }

  /* ------------------------------------------------------------------ */
  /* Schwierigkeit filter                                                */
  /* ------------------------------------------------------------------ */

  setupSchwierigkeitFilter() {
    // Use event delegation to handle clicks (works even if buttons are in dropdowns)
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-schwierigkeit-filter]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      const value = button.getAttribute('data-schwierigkeit-filter');
      this.toggleGroupParam('difficulty', value);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Dauer filter (range slider)                                         */
  /* ------------------------------------------------------------------ */

  setupDauerFilter() {
    const sliderContainers = document.querySelectorAll(
      '[data-blog-filter="dauer"]'
    );
    if (sliderContainers.length === 0) return;

    sliderContainers.forEach(sliderContainer => {
      const slider = sliderContainer.querySelector('[data-slider]');
      const tooltip = sliderContainer.querySelector('[data-tooltip]');
      const tooltipValue = sliderContainer.querySelector(
        '[data-tooltip-value]'
      );

      if (!slider) return;

      // Parse data from Liquid
      const values = JSON.parse(slider.dataset.values || '[]');
      const labels = JSON.parse(slider.dataset.labels || '[]');

      // Sync slider position with URL
      const initialDauer = this.getParams().get('duration');
      if (initialDauer) {
        const dauerNum = parseInt(initialDauer, 10);
        const index = values.findIndex(v => parseInt(v, 10) === dauerNum);
        if (index !== -1) {
          slider.value = index;
        }
      }

      this.updateDauerSliderVisuals(slider, tooltip, tooltipValue, labels);

      // Visual feedback while dragging
      slider.addEventListener('input', () => {
        this.updateDauerSliderVisuals(slider, tooltip, tooltipValue, labels);
      });

      // Apply filter on release
      slider.addEventListener('change', () => {
        const index = parseInt(slider.value, 10);
        const dauerValue = parseInt(values[index], 10);
        this.setParams(params => {
          if (Number.isNaN(dauerValue)) {
            params.delete('duration');
          } else {
            params.set('duration', String(dauerValue));
          }
        });
      });

      // Show tooltip on hover/focus
      sliderContainer.addEventListener('mouseenter', () => {
        if (tooltip) tooltip.style.opacity = '1';
      });

      sliderContainer.addEventListener('mouseleave', () => {
        if (tooltip) tooltip.style.opacity = '0';
      });

      slider.addEventListener('focus', () => {
        if (tooltip) tooltip.style.opacity = '1';
      });

      slider.addEventListener('blur', () => {
        if (tooltip) tooltip.style.opacity = '0';
      });
    });
  }

  /**
   * Update slider visual state (tooltip position, background)
   */
  // eslint-disable-next-line class-methods-use-this
  updateDauerSliderVisuals(slider, tooltip, tooltipValue, labels) {
    if (!slider) return;

    const index = parseInt(slider.value, 10);
    const max = parseInt(slider.max, 10);
    const percentage = (index / max) * 100;

    // Update tooltip text and position
    if (tooltipValue && labels[index]) {
      // eslint-disable-next-line no-param-reassign
      tooltipValue.textContent = labels[index];
    }

    if (tooltip) {
      const sliderWidth = slider.offsetWidth;
      const thumbWidth = 16;
      const offset =
        (percentage / 100) * (sliderWidth - thumbWidth) + thumbWidth / 2;
      const tooltipWidth = tooltip.offsetWidth;
      const centeredOffset = offset - tooltipWidth / 2;
      // eslint-disable-next-line no-param-reassign
      tooltip.style.left = `${centeredOffset}px`;
    }

    // Update slider background
    // eslint-disable-next-line no-param-reassign
    slider.style.background = `linear-gradient(to right,
      var(--color-erbse) 0%,
      var(--color-erbse) ${percentage}%,
      #D9D9D9 ${percentage}%,
      #D9D9D9 100%)`;
  }
}

/* -------------------------------------------------------------------- */
/* Helpers                                                              */
/* -------------------------------------------------------------------- */

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function truncateText(value, maxLength) {
  const text = String(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

function sizedImageUrl(url, size) {
  if (!url || !url.includes('cdn.shopify.com')) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}width=${size}&height=${size}&crop=center`;
}

// Initialize when DOM is ready
function initializeBlogFilters() {
  return new BlogArticleFilters();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeBlogFilters);
} else {
  initializeBlogFilters();
}
